import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from 'components/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { reservationService } from 'services/reservationService';
import { reviewService, MyReview } from 'services/reviewService';
import {
  Container,
  Header,
  BackButton,
  TabContainer,
  Tab,
  Content,
  ReviewCard,
  ReviewDate,
  ReviewHeader,
  ReviewCreatedDate,
  StarRating,
  MoreButton,
  ReviewContent,
  FacilityImage,
  FacilityImagePlaceholder,
  FacilityInfo,
  FacilityName,
  FacilityLocation,
  ReviewText,
  WriteReviewButton,
  EmptyMessage,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  MenuItem
} from './ReviewManagement.styles';

interface ReviewItem {
  id: number;
  facilityId: number;
  facilityName: string;
  facilityLocation: string;
  facilityImage?: string;
  usageDate: string;
}

const ReviewManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'write' | 'written'>('write');
  const [pendingReviews, setPendingReviews] = useState<ReviewItem[]>([]);
  const [writtenReviews, setWrittenReviews] = useState<MyReview[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isWrittenLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 리뷰 작성 대기 목록 조회
        const reservations = await reservationService.getPendingReviewReservations();
        const reviews: ReviewItem[] = reservations.map(reservation => {
          const startDate = new Date(reservation.startTime);
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');

          const locationParts = [
            reservation.facility.building?.name,
            reservation.facility.location
          ].filter(Boolean);

          return {
            id: reservation.id,
            facilityId: reservation.facility.id,
            facilityName: reservation.facility.name,
            facilityLocation: locationParts.join(' '),
            facilityImage: reservation.facility.imageUrl || undefined,
            usageDate: `${year}.${month}.${day}`
          };
        });
        setPendingReviews(reviews);

        // 작성한 리뷰 목록 조회 (개수 표시를 위해)
        const writtenData = await reviewService.getMyReviews();
        const mappedData = writtenData.map((review: any) => ({
          ...review,
          starRating: review.startRating || review.starRating
        }));
        setWrittenReviews(mappedData);
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleWriteReview = (reservationId: number) => {
    navigate(`/reviews/create/${reservationId}`);
  };

  const handleMoreClick = (reviewId: number) => {
    setSelectedReviewId(reviewId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReviewId(null);
  };

  const handleEditReview = () => {
    if (selectedReviewId) {
      navigate(`/reviews/edit/${selectedReviewId}`);
    }
    handleCloseModal();
  };

  const handleDeleteReview = async () => {
    if (!selectedReviewId) return;

    if (!window.confirm('리뷰를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await reviewService.deleteReview(selectedReviewId);
      alert('리뷰가 삭제되었습니다.');

      // 삭제된 리뷰를 목록에서 제거
      setWrittenReviews(writtenReviews.filter(review => review.id !== selectedReviewId));
      handleCloseModal();
    } catch (error: any) {
      console.error('리뷰 삭제 실패:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('리뷰 삭제에 실패했습니다. 다시 시도해주세요.');
      }
      handleCloseModal();
    }
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  return (
    <>
      <Container>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          리뷰 관리
        </Header>

        <TabContainer>
          <Tab
            active={activeTab === 'write'}
            onClick={() => setActiveTab('write')}
          >
            리뷰 작성하기 ({pendingReviews.length})
          </Tab>
          <Tab
            active={activeTab === 'written'}
            onClick={() => setActiveTab('written')}
          >
            작성한 리뷰 ({writtenReviews.length})
          </Tab>
        </TabContainer>

        <Content>
          {activeTab === 'write' ? (
            isLoading ? (
              <EmptyMessage>로딩 중...</EmptyMessage>
            ) : pendingReviews.length > 0 ? (
              pendingReviews.map((review) => (
                <ReviewCard key={review.id}>
                  <ReviewDate>이용일 {review.usageDate}</ReviewDate>
                  <ReviewContent>
                    {review.facilityImage ? (
                      <FacilityImage src={review.facilityImage} alt={review.facilityName} />
                    ) : (
                      <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
                    )}
                    <FacilityInfo>
                      <FacilityName>{review.facilityName}</FacilityName>
                      <FacilityLocation>{review.facilityLocation}</FacilityLocation>
                    </FacilityInfo>
                    <WriteReviewButton onClick={() => handleWriteReview(review.id)}>
                      리뷰 작성
                    </WriteReviewButton>
                  </ReviewContent>
                </ReviewCard>
              ))
            ) : (
              <EmptyMessage>작성 가능한 리뷰가 없습니다.</EmptyMessage>
            )
          ) : (
            isWrittenLoading ? (
              <EmptyMessage>로딩 중...</EmptyMessage>
            ) : writtenReviews.length > 0 ? (
              writtenReviews.map((review) => {
                const locationParts = [
                  review.facility.building?.name,
                ].filter(Boolean);

                return (
                  <ReviewCard key={review.id}>
                    <ReviewHeader>
                      <ReviewCreatedDate>작성일 {formatDate(review.createdAt)}</ReviewCreatedDate>
                      <StarRating>{renderStars(review.starRating)}</StarRating>
                    </ReviewHeader>
                    <ReviewContent>
                      {review.facility.imageUrl ? (
                        <FacilityImage src={review.facility.imageUrl} alt={review.facility.name} />
                      ) : (
                        <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
                      )}
                      <FacilityInfo>
                        <FacilityName>{review.facility.name}</FacilityName>
                        <FacilityLocation>{locationParts.join(' ')}</FacilityLocation>
                        <ReviewText>{review.content}</ReviewText>
                      </FacilityInfo>
                      <MoreButton onClick={() => handleMoreClick(review.id)}>⋮</MoreButton>
                    </ReviewContent>
                  </ReviewCard>
                );
              })
            ) : (
              <EmptyMessage>작성한 리뷰가 없습니다.</EmptyMessage>
            )
          )}
        </Content>
      </Container>

      <ModalOverlay isOpen={isModalOpen} onClick={handleCloseModal} />
      <ModalContent isOpen={isModalOpen}>
        <ModalHeader>
          <CloseButton onClick={handleCloseModal}>✕</CloseButton>
        </ModalHeader>
        <MenuItem onClick={handleEditReview}>리뷰 수정</MenuItem>
        <MenuItem onClick={handleDeleteReview}>리뷰 삭제</MenuItem>
      </ModalContent>

      <NavigationBar />
    </>
  );
};

export default ReviewManagement;
